/**
 * Debt Fight Club — Design System
 *
 * Derived from the Spartan skull illustration.
 * FLAT. BOLD. MILITANT. No gradients. No glow. No glass. Solid blocks.
 *
 * The 5 brand colors are the ONLY colors in the illustration:
 *   Crimson background, Burgundy shadows, Cream bone, Copper metal, Charcoal steel.
 */

// ─── BRAND PALETTE (exactly the illustration) ────────────
export const brand = {
  crimson:   '#7B1A2C',
  burgundy:  '#32050C',
  cream:     '#F9F2E8',
  copper:    '#B25533',
  charcoal:  '#404143',
} as const;

// ─── FLAT COLOR SCALE (no transparency — solid steps) ────
export const colors = {
  primary: {
    100: '#E8C1C8',
    200: '#C4505E',
    300: '#A42E3C',
    400: '#8C2233',
    500: '#7B1A2C', // crimson
    600: '#6A1525',
    700: '#58101E',
    800: '#470C18',
    900: '#32050C', // burgundy
  },
  secondary: {
    200: '#D4824E',
    300: '#C36C38',
    500: '#B25533', // copper
    700: '#823A23',
    900: '#522113',
  },
  neutral: {
    50:  '#F9F2E8', // cream
    100: '#F0E6D8',
    200: '#E0D3C0',
    300: '#C8B9A4',
    400: '#A09080',
    500: '#706660',
    600: '#555050',
    700: '#404143', // charcoal
    800: '#2E2E30',
    900: '#1C1C1E',
    950: '#111112',
  },
  success: '#46A758',
  warning: '#C48818',
  error:   '#B92D3A',
  info:    '#3D77B5',
} as const;

// ─── SEMANTIC TOKENS ─────────────────────────────────────
// Page background is burgundy-black, not generic #0D0D0E.
// Surfaces are charcoal, not grey. Cream is the text.
export const semantic = {
  text: {
    primary:   brand.cream,
    secondary: colors.neutral[300],  // #C8B9A4
    muted:     colors.neutral[400],  // #A09080
    disabled:  colors.neutral[500],
    inverse:   brand.burgundy,
    accent:    brand.crimson,
    copper:    brand.copper,
  },
  bg: {
    page:      brand.burgundy,        // deep burgundy — like the illustration bg
    surface:   colors.neutral[900],   // #1C1C1E — card bg
    elevated:  colors.neutral[800],   // #2E2E30
    input:     colors.neutral[950],   // #111112
    crimson:   brand.crimson,         // solid crimson section bg
    copper:    brand.copper,          // solid copper accent bg
    cream:     brand.cream,           // light section bg
  },
  border: {
    default:   colors.neutral[700],   // charcoal
    strong:    brand.crimson,
    copper:    brand.copper,
    cream:     brand.cream,
  },
  action: {
    primary:        brand.crimson,
    primaryHover:   colors.primary[300],
    secondary:      brand.copper,
    secondaryHover: colors.secondary[300],
    danger:         colors.error,
    success:        colors.success,
  },
} as const;

// ─── TYPOGRAPHY ──────────────────────────────────────────
// Oswald for EVERYTHING bold. Big. Caps. Like a military poster.
export const typography = {
  fontFamily: {
    display: "'Oswald', 'Impact', sans-serif",
    sans:    "'Inter', -apple-system, sans-serif",
    mono:    "'JetBrains Mono', 'SF Mono', monospace",
  },
  fontSize: {
    xs:   '0.75rem',
    sm:   '0.875rem',
    base: '1rem',
    lg:   '1.125rem',
    xl:   '1.25rem',
    '2xl': '1.5rem',
    '3xl': '2rem',
    '4xl': '2.5rem',
    '5xl': '3.5rem',
    '6xl': '5rem',    // landing page hero
  },
  fontWeight: {
    regular:  400,
    medium:   500,
    semibold: 600,
    bold:     700,
    black:    900,
  },
  lineHeight: {
    tight:   1.1,
    snug:    1.25,
    normal:  1.5,
    relaxed: 1.65,
  },
  letterSpacing: {
    tight:  '-0.02em',
    normal: '0',
    wide:   '0.05em',
    wider:  '0.12em',
    widest: '0.2em',
  },
} as const;

// ─── SPACING ─────────────────────────────────────────────
export const spacing = {
  0:  '0',
  1:  '0.25rem',
  2:  '0.5rem',
  3:  '0.75rem',
  4:  '1rem',
  5:  '1.25rem',
  6:  '1.5rem',
  8:  '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
} as const;

// ─── BORDERS (thick, militant) ───────────────────────────
export const borders = {
  thin:  '1px solid',
  medium: '2px solid',
  thick: '3px solid',
  heavy: '4px solid',
} as const;

// ─── RADII (minimal — poster aesthetic, not SaaS) ───────
export const radii = {
  none: '0',
  sm:   '2px',
  md:   '4px',
  full: '9999px',
} as const;

// ─── SHADOWS (minimal — flat design, not elevated) ──────
// Only used for dropdown/modal layering, never for depth illusion
export const shadows = {
  none: 'none',
  sm:   '0 2px 4px rgba(50, 5, 12, 0.2)',
  md:   '0 4px 12px rgba(50, 5, 12, 0.3)',
} as const;

// ─── TRANSITIONS (snappy, not floaty) ───────────────────
export const transitions = {
  fast:   '100ms ease',
  normal: '200ms ease',
} as const;

// ─── Z-INDEX ─────────────────────────────────────────────
export const zIndex = {
  base:     0,
  dropdown: 100,
  sticky:   200,
  overlay:  300,
  modal:    400,
  header:   999,
} as const;

// ─── CHART COLORS (solid, from the palette) ─────────────
export const chartColors = [
  brand.crimson,
  brand.copper,
  '#C48818',
  colors.success,
  '#3D77B5',
  brand.charcoal,
] as const;

// ─── VARIANT MAPS ───────────────────────────────────────
export const variantColors = {
  danger:   brand.crimson,
  warning:  brand.copper,
  success:  colors.success,
  neutral:  colors.neutral[300],
  critical: brand.crimson,
  tip:      colors.info,
} as const;

export const priorityColors = {
  high:   brand.crimson,
  medium: brand.copper,
  low:    colors.neutral[400],
} as const;

export const insightConfig = {
  critical: { color: brand.crimson,  bg: brand.burgundy,      label: 'CRITICAL' },
  warning:  { color: brand.copper,   bg: colors.secondary[900], label: 'WARNING' },
  success:  { color: colors.success, bg: '#1A2E1D',           label: 'VICTORY' },
  tip:      { color: colors.info,    bg: '#1A2535',           label: 'INTEL' },
} as const;

// ─── COMBINED THEME ─────────────────────────────────────
export const theme = {
  brand,
  colors,
  semantic,
  typography,
  spacing,
  borders,
  radii,
  shadows,
  transitions,
  zIndex,
  chartColors,
  variantColors,
  priorityColors,
  insightConfig,
} as const;

export type Theme = typeof theme;
export default theme;
